import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { purchasesApi, suppliersApi, productsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import ProductSearchDialog from '../components/ProductSearchDialog';
import { Trash2, Save, ArrowLeft, PackageSearch, Warehouse } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Form unificato create/edit DDT Acquisto (carico magazzino).
 * - I prodotti vengono aggiunti tramite ricerca rapida (codice/descrizione).
 * - Si lavora in COSTO (cost), il listino non si applica.
 * - Alla conferma il backend incrementa lo stock dei prodotti.
 */
const PurchaseFormPage = () => {
  const navigate = useNavigate();
  const { id: purchaseId } = useParams();
  const isEdit = Boolean(purchaseId);

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [supplierId, setSupplierId] = useState('');
  const [supplierDocNumber, setSupplierDocNumber] = useState('');
  const [supplierDocDate, setSupplierDocDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [items, setItems] = useState([]);
  const [purchase, setPurchase] = useState(null);

  // Product picker (dialog stile DDT)
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => { bootstrap(); }, []); // eslint-disable-line

  const bootstrap = async () => {
    try {
      const [s, p] = await Promise.all([suppliersApi.getAll(), productsApi.getAll()]);
      setSuppliers(s.data); setProducts(p.data);
      if (isEdit) {
        const r = await purchasesApi.getById(purchaseId);
        const pd = r.data;
        setPurchase(pd);
        setSupplierId(pd.supplier_id || '');
        setSupplierDocNumber(pd.supplier_doc_number || '');
        setSupplierDocDate(pd.supplier_doc_date || new Date().toISOString().split('T')[0]);
        setNote(pd.note || '');
        setPaidAmount(pd.paid_amount ? String(pd.paid_amount) : '');
        setItems(pd.items || []);
      }
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  const supplier = suppliers.find(s => s.id === supplierId);

  // Riceve (product, boxes, explicitPieces) dal ProductSearchDialog e crea una riga di acquisto basata sul COSTO.
  const addProduct = (p, boxes, explicitPieces) => {
    const b = Math.round((parseFloat(boxes) || 0) * 100) / 100;
    if (b <= 0) return;
    const ppb = parseInt(p.pieces_per_box) || 1;
    const pieces = (explicitPieces != null)
      ? Math.round(parseFloat(explicitPieces) || 0)
      : Math.round(b * ppb);
    const cost = p.cost || 0;
    setItems(prev => [...prev, {
      product_id: p.id,
      product_code: p.code,
      description: p.description,
      boxes: b, pieces_per_box: ppb, pieces,
      cost, iva: p.iva || 22,
      total: pieces * cost,
    }]);
  };

  const updateItem = (i, field, value) => {
    const ni = [...items]; ni[i][field] = value;
    const it = ni[i];
    const ppb = parseFloat(it.pieces_per_box) || 1;
    const b = parseFloat(it.boxes) || 0;
    const px = parseFloat(it.pieces) || 0;
    if (field === 'boxes') it.pieces = Math.round(b * ppb);
    else if (field === 'pieces') {
      it.pieces = Math.round(px);
      it.boxes = ppb > 0 ? Math.round((it.pieces / ppb) * 100) / 100 : 0;
    }
    else if (field === 'pieces_per_box') it.pieces = Math.round(b * ppb);
    it.total = (parseFloat(it.pieces) || 0) * (parseFloat(it.cost) || 0);
    setItems(ni);
  };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totals = {
    total_boxes: items.reduce((s, i) => s + (parseFloat(i.boxes) || 0), 0),
    total_pieces: items.reduce((s, i) => s + (parseFloat(i.pieces) || 0), 0),
    total_amount: items.reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
  };
  const fmt = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const buildPayload = () => ({
    supplier_id: supplier?.id || '',
    supplier_name: supplier ? ((supplier.nickname?.trim()) || supplier.name) : '',
    supplier_piva: supplier?.piva || '',
    supplier_doc_number: supplierDocNumber,
    supplier_doc_date: supplierDocDate,
    items: items.map(it => ({
      product_id: it.product_id, product_code: it.product_code, description: it.description,
      boxes: parseFloat(it.boxes) || 0, pieces_per_box: parseInt(it.pieces_per_box) || 0,
      pieces: parseFloat(it.pieces) || 0,
      cost: parseFloat(it.cost) || 0, iva: parseFloat(it.iva) || 22,
      total: parseFloat(it.total) || 0,
    })),
    total_boxes: totals.total_boxes, total_pieces: totals.total_pieces,
    total_amount: totals.total_amount,
    paid_amount: parseFloat(paidAmount) || 0,
    note,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplier) { toast.error('Seleziona un fornitore'); return; }
    if (items.length === 0) { toast.error('Aggiungi almeno un prodotto'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await purchasesApi.update(purchaseId, buildPayload());
        toast.success('DDT Acquisto aggiornato (stock ricalcolato)');
      } else {
        const r = await purchasesApi.create(buildPayload());
        toast.success(`DDT Acquisto AC-${r.data.number}/${r.data.year} creato. Stock aggiornato.`);
      }
      navigate('/acquisti');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Caricamento...</div>;

  return (
    <div data-testid="purchase-form-page" className="space-y-4 max-w-6xl mx-auto">
      <ProductSearchDialog open={searchOpen} onOpenChange={setSearchOpen} products={products} onInsert={addProduct} existingItems={items} />

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/acquisti')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">
            {isEdit ? `DDT Acquisto AC-${purchase?.number}/${purchase?.year}` : 'Nuovo DDT Acquisto'}
          </h1>
          <p className="text-muted-foreground text-sm flex items-center gap-1">
            <Warehouse className="w-3.5 h-3.5" /> Carico magazzino — incrementa lo stock dei prodotti
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="card-swiss">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold font-['Manrope']">Fornitore e documento</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Fornitore *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger data-testid="purchase-supplier-select" className="h-9">
                    <SelectValue placeholder="Seleziona fornitore..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nickname || s.name} {s.piva ? ` · ${s.piva}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">N° doc. fornitore</Label>
                <Input data-testid="purchase-doc-number" value={supplierDocNumber} onChange={(e) => setSupplierDocNumber(e.target.value)} placeholder="DDT/Fattura del fornitore" className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data doc. fornitore</Label>
                <Input type="date" value={supplierDocDate} onChange={(e) => setSupplierDocDate(e.target.value)} className="h-9 text-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Acconto pagato</Label>
                <Input type="number" step="0.01" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} placeholder="0.00" className="h-9 text-sm" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold font-['Manrope']">Prodotti da caricare</CardTitle>
              <Button type="button" onClick={() => setSearchOpen(true)} data-testid="open-purchase-picker-btn" className="h-9 bg-green-600 hover:bg-green-700 text-white">
                <PackageSearch className="w-4 h-4 mr-2" /> Cerca e Aggiungi Prodotti
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[36px] text-center">#</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[90px]">Codice</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2">Descrizione</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[70px] text-center">Box</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[55px] text-center">Pz/B</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[70px] text-center">Pezzi</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[90px] text-right">Costo unit.</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[100px] text-right">Totale</TableHead>
                      <TableHead className="h-8 px-1 w-[32px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, i) => (
                      <TableRow key={i} className="hover:bg-muted/30">
                        <TableCell className="py-1 px-2 text-sm font-['JetBrains_Mono'] font-semibold text-center text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="py-1 px-2 text-sm font-['JetBrains_Mono'] font-semibold">{item.product_code}</TableCell>
                        <TableCell className="py-1 px-2">
                          <Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="h-8 text-sm" />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input data-testid={`purchase-boxes-${i}`} type="number" step="any" min="0" value={item.boxes} onChange={(e) => updateItem(i, 'boxes', e.target.value)} className="cell-input h-8 text-center font-['JetBrains_Mono'] text-sm px-1 font-semibold" />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input type="number" step="1" min="0" value={item.pieces_per_box} onChange={(e) => updateItem(i, 'pieces_per_box', e.target.value)} className="h-8 text-center font-['JetBrains_Mono'] text-sm px-1" />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input type="number" step="any" min="0" value={item.pieces} onChange={(e) => updateItem(i, 'pieces', e.target.value)} className="cell-input h-8 text-center font-['JetBrains_Mono'] text-sm px-1" />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input data-testid={`purchase-cost-${i}`} type="number" step="0.01" min="0" value={item.cost} onChange={(e) => updateItem(i, 'cost', e.target.value)} className="h-8 text-right font-['JetBrains_Mono'] text-sm px-1" />
                        </TableCell>
                        <TableCell className="py-1 px-2 text-right font-['JetBrains_Mono'] text-sm font-semibold">{fmt(item.total)}</TableCell>
                        <TableCell className="py-1 px-1 text-center">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(i)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">Nessun prodotto. Usa "Cerca e Aggiungi" per inserire le voci.</div>
            )}
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="py-3"><CardTitle className="text-sm font-semibold font-['Manrope']">Note</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Annotazioni interne sul carico" />
          </CardContent>
        </Card>

        <div className="sticky bottom-0 -mx-2 px-2 py-3 bg-background/95 backdrop-blur border-t border-border z-10">
          <div className="flex items-center justify-between flex-wrap gap-3 max-w-6xl mx-auto">
            <div className="text-sm">
              <span className="text-muted-foreground mr-3">Totale acquisto:</span>
              <span className="font-bold text-lg font-['JetBrains_Mono']">{fmt(totals.total_amount)}</span>
              <span className="text-muted-foreground ml-4">Box: <b>{totals.total_boxes}</b></span>
            </div>
            <Button type="submit" disabled={saving} className="btn-primary bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-purchase-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : (isEdit ? 'AGGIORNA E RICARICA STOCK' : 'SALVA E CARICA STOCK')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseFormPage;
